import dayjs from "dayjs";
import {
  Archive,
  Calendar,
  CheckCircle,
  Clock,
  Edit,
  Eye,
  Package,
  Settings,
  Trash2,
  User,
  RotateCcw,
} from "lucide-react";
import { FC, useState } from "react";
import { PaginationResponse } from "~/services/type";
import { Task } from "~/types/task";
import { Priority, TaskStatus } from "~/utils/constants";
import { useTaskStore } from "./task.store";
import { List, Spin, Tabs } from "antd";
import { AddNote } from "./add-note";
import { TASK_KEYS, useDeleteTask, useUpdateTask } from "~/services/task";
import { useQueryClient } from "@tanstack/react-query";
import { useNotification } from "~/hooks";
import { ViewTaskModal } from "./view-task-modal";

type Props = {
  tasks?: PaginationResponse<Task>;
  isLoading?: boolean;
};

export const TableTasks: FC<Props> = ({ tasks, isLoading }) => {
  const { filters, setFilters } = useTaskStore();
  const [showArchiveList, setShowArchiveList] = useState(false);

  const activeTasks = tasks?.items.filter((task) => !task.isArchived) || [];
  const archivedTasks = tasks?.items.filter((task) => task.isArchived) || [];

  const dataSource = showArchiveList ? archivedTasks : activeTasks;

  return (
    <div className="divide-y">
      <div className="mb-6">
        <div className="p-4 border-b bg-white">
          <Tabs
            activeKey={showArchiveList ? "archived" : "active"}
            onChange={(key) => setShowArchiveList(key === "archived")}
            items={[
              { key: "active", label: "Active Tasks" },
              { key: "archived", label: "Archived Tasks" },
            ]}
          />
        </div>
        <List
          loading={isLoading}
          itemLayout="horizontal"
          pagination={{
            current: filters.page,
            pageSize: filters.pageSize,
            total: dataSource.length,
            onChange: (page, pageSize) => setFilters({ page, pageSize }),
            position: "bottom",
            align: "center",
            className: "mb-6",
          }}
          dataSource={dataSource}
          rowKey={(row) => row.id}
          renderItem={(task) => (
            <ItemRender task={task} isArchivedView={showArchiveList} />
          )}
        />
        <ViewTaskModal /> {/* Add modal for viewing details */}
      </div>
    </div>
  );
};

const getStatusBadge = (status: TaskStatus) => {
  switch (status) {
    case TaskStatus.COMPLETED:
      return (
        <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
          Completed
        </span>
      );
    case TaskStatus.IN_PROGRESS:
      return (
        <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
          In Progress
        </span>
      );
    case TaskStatus.PENDING:
      return (
        <span className="px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">
          Pending
        </span>
      );
    default:
      return (
        <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800">
          {status}
        </span>
      );
  }
};

const getPriorityIndicator = (priority: Priority) => {
  switch (priority) {
    case Priority.HIGH:
      return (
        <span className="px-2 py-1 rounded-full text-xs bg-red-100 text-red-800">
          High
        </span>
      );
    case Priority.MEDIUM:
      return (
        <span className="px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">
          Medium
        </span>
      );
    case Priority.LOW:
      return (
        <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
          Low
        </span>
      );
    default:
      return (
        <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800">
          {priority}
        </span>
      );
  }
};

const getTaskTypeIcon = (type: string) => {
  switch (type) {
    case "maintenance":
      return <Settings size={16} />;
    case "inventory":
      return <Package size={16} />;
    case "meeting":
      return <User size={16} />;
    case "artwork":
      return <Package size={16} />;
    default:
      return <Package size={16} />;
  }
};

const ItemRender = ({
  task,
  isArchivedView,
}: {
  task: Task;
  isArchivedView: boolean;
}) => {
  const { openTaskModal, openViewTaskModal } = useTaskStore();
  const [expandedTask, setExpandedTask] = useState<string>();
  const qc = useQueryClient();
  const { success, error } = useNotification();
  const mutation = useUpdateTask();
  const mutationDelete = useDeleteTask();

  const completeTask = (taskId: string) => {
    const formData = new FormData();
    formData.append("taskStatus", TaskStatus.COMPLETED);
    mutation.mutate(
      { taskId, payload: formData },
      {
        onSuccess: () => {
          qc.invalidateQueries({
            queryKey: TASK_KEYS.GET_TASKS({}),
          });
          success({ message: "Task finished!" });
        },
        onError: (err) => {
          error({ message: "Can not finish task", description: err.message });
        },
      }
    );
  };

  const archiveTask = (taskId: string) => {
    const formData = new FormData();
    formData.append("isArchived", "true");
    mutation.mutate(
      { taskId, payload: formData },
      {
        onSuccess: () => {
          qc.invalidateQueries({
            queryKey: TASK_KEYS.GET_TASKS({}),
          });
          success({ message: "Task archived!" });
        },
        onError: (err) => {
          error({ message: "Can not archive task", description: err.message });
        },
      }
    );
  };

  const unarchiveTask = (taskId: string) => {
    const formData = new FormData();
    formData.append("isArchived", "false");
    mutation.mutate(
      { taskId, payload: formData },
      {
        onSuccess: () => {
          qc.invalidateQueries({
            queryKey: TASK_KEYS.GET_TASKS({}),
          });
          success({ message: "Task unarchived!" });
        },
        onError: (err) => {
          error({
            message: "Can not unarchive task",
            description: err.message,
          });
        },
      }
    );
  };

  const deleteTask = (taskId: string) => {
    mutationDelete.mutate(taskId, {
      onSuccess: () => {
        qc.invalidateQueries({
          queryKey: TASK_KEYS.GET_TASKS({}),
        });
        success({ message: "Delete task successfully!" });
      },
      onError: (err) => {
        error({ message: "Can not delete task!", description: err.message });
      },
    });
  };

  const handleExpandTask = (taskId: string) => {
    setExpandedTask(expandedTask === taskId ? "" : taskId);
  };

  return (
    <Spin spinning={mutation.isPending || mutationDelete.isPending}>
      <div key={task.id} className="border-b">
        <div className="p-4">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <div
                onClick={() => handleExpandTask(task.id)}
                className="flex items-center space-x-2 mb-2 cursor-pointer"
              >
                <div
                  className={`p-1 rounded ${
                    task.taskStatus === TaskStatus.COMPLETED
                      ? "bg-green-100"
                      : task.priority === Priority.HIGH
                      ? "bg-red-100"
                      : "bg-blue-100"
                  }`}
                >
                  {getTaskTypeIcon(task.taskType)}
                </div>
                <div className="font-medium">{task.title}</div>
              </div>
              <div className="text-sm text-gray-600 mb-2">
                {task.description}
              </div>
              <div className="flex items-center flex-wrap gap-2 text-sm">
                <div className="flex items-center space-x-1 text-gray-500">
                  <Calendar size={14} />
                  <span>{dayjs(task.dueDate).format("YYYY-MM-DD")}</span>
                </div>
                <div className="flex items-center space-x-1 text-gray-500">
                  <Clock size={14} />
                  {task.startTime && <span>{task.startTime}</span>}
                </div>
                {task.assignees && task.assignees.length > 0 && (
                  <div className="flex items-center space-x-1 text-gray-500">
                    <User size={14} />
                    <span>
                      {task.assignees.length > 2
                        ? `${task.assignees[0].user?.username} +${
                            task.assignees.length - 1
                          }`
                        : `${task.assignees[0].user?.username}${
                            task.assignees[1]
                              ? ` , ${task.assignees[1].user?.username}`
                              : ""
                          }`}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col items-end space-y-2">
              <div className="flex items-center space-x-2">
                {getStatusBadge(task.taskStatus)}
                {getPriorityIndicator(task.priority)}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => openViewTaskModal(task.id)}
                  className="p-1 text-gray-400 hover:text-blue-600"
                >
                  <Eye size={16} />
                </button>
                <button
                  onClick={() => openTaskModal(task.id)}
                  className="p-1 text-gray-400 hover:text-blue-600"
                >
                  <Edit size={16} />
                </button>
                {task.taskStatus !== TaskStatus.COMPLETED && (
                  <button
                    onClick={() => completeTask(task.id)}
                    className="p-1 text-gray-400 hover:text-green-600"
                  >
                    <CheckCircle size={16} />
                  </button>
                )}
                {isArchivedView ? (
                  <button
                    onClick={() => unarchiveTask(task.id)}
                    className="p-1 text-gray-400 hover:text-blue-600"
                  >
                    <RotateCcw size={16} />
                  </button>
                ) : (
                  <button
                    onClick={() => archiveTask(task.id)}
                    className="p-1 text-gray-400 hover:text-yellow-600"
                  >
                    <Archive size={16} />
                  </button>
                )}
                <button
                  onClick={() => deleteTask(task.id)}
                  className="p-1 text-gray-400 hover:text-red-600"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          </div>
          {expandedTask === task.id && (
            <div className="mt-4 pl-8 border-t pt-4">
              {task.inventories && task.inventories.length > 0 && (
                <div className="mb-4">
                  <div className="text-sm font-medium mb-2">Related Items</div>
                  <div className="flex flex-wrap gap-2">
                    {task.inventories.map((item, index) => (
                      <div
                        key={index}
                        className="px-3 py-1 bg-gray-100 rounded-lg text-sm"
                      >
                        {item.inventoryRef}: {item.title}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="mb-4">
                <div className="text-sm font-medium mb-2">Notes</div>
                {task.notes && task.notes.length > 0 ? (
                  <div className="space-y-2">
                    {task.notes.map((note, index) => (
                      <div key={index} className="bg-gray-50 p-3 rounded-lg">
                        <div className="text-sm">{note.content}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          {note.author.username} -{" "}
                          {dayjs(note.createdAt).format("YYYY-MM-DD HH:mm")}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-gray-500 text-sm">No notes yet</div>
                )}
              </div>
              {task.taskStatus === TaskStatus.COMPLETED && (
                <div className="flex items-center space-x-2 text-green-600 mb-4">
                  <CheckCircle size={16} />
                  <span>
                    Completed by {task.completedBy} on {task.completedDate}
                  </span>
                </div>
              )}
              <AddNote task={task} />
            </div>
          )}
        </div>
      </div>
    </Spin>
  );
};
